import { Box, DialogActions, DialogContent, Grid, IconButton, Stack, useMediaQuery, useTheme } from "@mui/material";
import React from "react";
import { AiOutlineReload } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { AppLogo } from "../../components/AppLogo";
import { StyledPageComment } from "../../components/StyledPageComment";
import { routes } from "../../constants/appRoutes";
import { useErrorNotify } from "../../contexts/AlertContext";
import { useDialogContext } from "../../contexts/DialogContext";
import { useLoaderContext } from "../../contexts/LoaderContext";
import { useProfileContext } from "../../contexts/ProfileContext";
import { GetContract, SendReminder, SetLegalRepresentativeRole } from "../../shared/api.helpers";
import { Common } from "../../shared/api.helpers.types";
import { errorMessages } from "../../shared/translations";
import { _ } from "../../shared/utils";
import { VdsButton, VdsDialog, VdsTextField } from "../../vds";
import { HTMLMainPromoFormatter } from "../ContractPage";

//#Page hooks
const useHomePage = () => {

    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up(700));

    const setLoader = useLoaderContext();
    const navigate = useNavigate();
    const errorNotify = useErrorNotify();
    const [profile] = useProfileContext();

    const [email, setEmail] = React.useState({ email: '', _isValid_email: '' });
    const [sectionsLoading, setSectionsLoading] = React.useState(true);
    const [contracts, setContracts] = React.useState<Common.ContentWithLocContent[]>([]);
    const [createLegalDialog, setCreateLegalDialog] = React.useState(false);

    React.useEffect(() => {
        handleGetContract();
    }, []);

    const handleGetContract = () => {

        setSectionsLoading(true);
        GetContract({
            companyOwnCode: profile?.company?.code,
            contextName: profile.campaign.SellingInfo.LcmsContractName
        })
            .then((res) => {
                setSectionsLoading(false);
                const code = res.data.status?.code;
                if (!["311", "300"].includes(code)) {
                    const contentWithLocContent = res.data
                        .contextsWithContents
                        .contextWithContents
                        .contentsWithLocContents
                        .contentWithLocContent;

                    setContracts(contentWithLocContent);
                } else {
                    errorNotify(`${errorMessages.GetContract[code] || errorMessages?.GeneralError} (Codice errore: ${code || 500})`);
                    setSectionsLoading(null);
                }
            })
            .catch((err) => {
                setSectionsLoading(null);
            });
    }
    const handleSendEmail = () => {

        const _isValid_email = _.verify.email2(email.email);
        setEmail({
            ...email,
            _isValid_email
        });

        if (!_isValid_email) {

            setLoader(true);
            SendReminder({
                params: {
                    email: email.email,
                    token: profile.query_token
                }
            })
                .then(function (res) {
                    const code = res.data.status?.code;
                    setLoader(false);
                    if (code === "0") {

                        navigate(`/${routes.sendReminder}`, { replace: true });
                    } else {
                        errorNotify(`${errorMessages.SendReminder[code] || errorMessages?.GeneralError} (Codice errore: ${code || 500})`);
                    }
                })
                .catch(function (error) {
                    const code = error.status?.code;
                    errorNotify(`${errorMessages.GeneralError} (Codice errore: ${code || 500})`);
                    setLoader(false);
                });
        }
    }
    const handleCreateLegalDialog = (state: boolean) => { setCreateLegalDialog(state); }
    const handleEmailChange = (ev) => {
        const email = ev.target.value;
        setEmail({ email, _isValid_email: _.verify.email2(email) });
    }

    return {
        models: {
            createLegalDialog,
            email,
            matches,
            profile,
            contracts,
            sectionsLoading
        },
        events: {
            handleGetContract,
            handleCreateLegalDialog,
            handleEmailChange,
            handleSendEmail,
        }
    }
}

export const HomePage = () => {

    const {
        models: {
            email,
            matches,
            profile,
            contracts,
            createLegalDialog,
            sectionsLoading
        },
        events: {
            handleGetContract,
            handleEmailChange,
            handleCreateLegalDialog,
            handleSendEmail,
        }
    } = useHomePage();

    return (
        <Box display="flex" gap={2} flexDirection="column" py={2} px={1} maxWidth="1850px">
            {!!contracts?.length && (<HTMLMainPromoFormatter profile={profile} contract={contracts[1]} />)}
            {(sectionsLoading === null) ? (
                <Box
                    sx={{
                        display: 'flex',
                        height: 'calc(100vh - 460px)',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 2
                    }}
                >
                    <IconButton onClick={handleGetContract}>
                        <AiOutlineReload fontSize={50} opacity={0.3} />
                    </IconButton>
                    <StyledPageComment>Si è verificato un problema con il caricamento della promozione.</StyledPageComment>
                </Box>
            ) : sectionsLoading ? (
                <Box
                    sx={{
                        display: 'flex',
                        height: 'calc(100vh - 460px)',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 2
                    }}
                >
                    <StyledPageComment>Caricamento del contratto in corso...</StyledPageComment>
                </Box>
            ) : null}
            <Box>
                <VdsButton
                    variant="contained"
                    color="primary"
                    onClick={() => handleCreateLegalDialog(true)}
                    sx={{ width: '350px' }}
                >
                    Sono il Legale Rappresentante
                </VdsButton>
            </Box>
            <StyledPageComment>
                Oppure inserisci l&#8217;indirizzo e-mail del Legale Rappresentante per comunicargli la promozione esclusiva
            </StyledPageComment>
            <Stack
                pb={2}
                spacing={2}
                direction={matches ? "row" : "column"}
            >
                <VdsButton
                    variant="contained"
                    color="primary"
                    onClick={handleSendEmail}
                    sx={{ width: '350px' }}
                >
                    Invia al Legale Rappresentante
                </VdsButton>
                <VdsTextField
                    value={email.email}
                    error={email._isValid_email !== ''}
                    errorText={email._isValid_email}
                    onChange={handleEmailChange}
                    placeholder="Inserisci qui l&#8217;indirizzo e-mail"
                    sx={{ width: '350px' }}
                    collapse
                />
            </Stack>
            <CreateLegalDialog open={createLegalDialog} handleCreateLegalDialog={handleCreateLegalDialog} />
        </Box>
    );
};

interface CreateLegalDialogProps {
    open: boolean;
    handleCreateLegalDialog: (state: boolean) => void;
}

interface Legal {
    name: string;
    surname: string;
    fiscalCode: string;
    pec: string;
    pecConfirm: string;
    isFiscalCodeReadOnly: boolean;
    isPECReadOnly: boolean;
    pecValidity: string;
    pecConfirmValidity: string;
    fiscalCodeValidity: string;
}

const CreateLegalDialog: React.FC<CreateLegalDialogProps> = ({ open, handleCreateLegalDialog }) => {

    const navigate = useNavigate();
    const setLoader = useLoaderContext();
    const errorNotify = useErrorNotify();

    const [profile, setProfile] = useProfileContext();

    const [legal, setLegal] = React.useState<Legal>({
        name: profile.name,
        surname: profile.surname,
        fiscalCode: profile.fiscalCode,
        pec: profile.companyPec,
        pecConfirm: profile.companyPec,
        isFiscalCodeReadOnly: !!profile.fiscalCode,
        isPECReadOnly: !!profile.companyPec,
        pecValidity: '',
        pecConfirmValidity: '',
        fiscalCodeValidity: ''
    });

    React.useEffect(() => {

        if (open) {
            setLegal({
                name: profile.name,
                surname: profile.surname,
                fiscalCode: profile.fiscalCode,
                pec: profile.companyPec,
                pecConfirm: profile.companyPec,
                isFiscalCodeReadOnly: !!profile.fiscalCode,
                isPECReadOnly: !!profile.companyPec,
                pecValidity: '',
                pecConfirmValidity: '',
                fiscalCodeValidity: ''
            });
        }
    }, [open]);

    const handleFiscalCodeChange = (ev) => {
        setLegal({
            ...legal,
            fiscalCode: ev.target.value,
            fiscalCodeValidity: _.verify.fiscalCode(ev.target.value)
        });
    }
    const handlePECChange = (ev) => {
        setLegal({
            ...legal,
            pec: ev.target.value,
            pecValidity: _.verify.email(ev.target.value),
            pecConfirmValidity: _.verify.confirmPec(legal.pecConfirm, ev.target.value)
        });
    }
    const handlePECConfirmChange = (ev) => {
        setLegal({
            ...legal,
            pecConfirm: ev.target.value,
            pecConfirmValidity: _.verify.confirmPec(ev.target.value, legal.pec),
        });
    }
    const genericChange = (ev) => {
        setLegal({ ...legal, [ev.target.name]: ev.target.value });
    }

    const handleSubmit = () => {

        const
            pecValidity = _.verify.email(legal.pec),
            pecConfirmValidity = _.verify.confirmPec(legal.pecConfirm, legal.pec),
            fiscalCodeValidity = _.verify.fiscalCode(legal.fiscalCode);

        setLegal({
            ...legal,
            pecValidity,
            pecConfirmValidity,
            fiscalCodeValidity
        });

        if (!(pecValidity || pecConfirmValidity || fiscalCodeValidity)) {

            setLoader(true);
            SetLegalRepresentativeRole({
                params: {
                    token: profile.query_token,
                    companyOid: profile.company.code,
                    pec: legal.pec,
                    user_fiscalCode: profile.fiscalCode ? null : legal.fiscalCode
                }
            })
                .then((res) => {
                    setLoader(false);
                    const code = res.data.status?.code;
                    if (code === "0") {
                        setProfile({
                            ...profile,
                            fiscalCode: legal.fiscalCode,
                            isLegalRepresentative: true,
                            company: {
                                ...profile.company,
                                pec: legal.pec,
                            }
                        });
                        navigate(`/${routes.app}/${routes.contract}?token=${profile.query_token}`, { replace: true });
                    } else {
                        errorNotify(`${errorMessages.SetLegalRepresentativeRole[code] || errorMessages?.GeneralError} (Codice errore: ${code || 500})`);
                    }
                })
                .catch(function (error) {
                    const code = error.status?.code;
                    errorNotify(`${errorMessages.GeneralError} (Codice errore: ${code || 500})`);
                    setLoader(false);
                });
        }
    }

    return (
        <VdsDialog open={open} maxWidth="sm">
            <AppLogo />
            <DialogContent sx={{ marginTop: '0px!important' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12}>
                        <StyledPageComment>
                            Dichiaro di essere il legale rappresentante e confermo la correttezza e completezza dei dati qui di seguito indicati:
                        </StyledPageComment>
                        <StyledPageComment>
                            <strong>{profile?.company?.name}</strong>, partita IVA <strong>{profile?.company?.vatCode}</strong>
                        </StyledPageComment>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <VdsTextField
                            value={legal.name || ''}
                            onChange={genericChange}
                            label="Nome*"
                            collapse
                            readOnly
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <VdsTextField
                            value={legal.surname || ''}
                            onChange={genericChange}
                            label="Cognome*"
                            collapse
                            readOnly
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <VdsTextField
                            value={legal.fiscalCode || ''}
                            onChange={handleFiscalCodeChange}
                            error={legal.fiscalCodeValidity !== ''}
                            errorText={legal.fiscalCodeValidity}
                            label="Codice Fiscale*"
                            readOnly={legal.isFiscalCodeReadOnly}
                            collapse
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <VdsTextField
                            value={legal.pec || ''}
                            onChange={handlePECChange}
                            error={legal.pecValidity !== ''}
                            errorText={legal.pecValidity}
                            label="PEC*"
                            readOnly={legal.isPECReadOnly}
                            collapse
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <VdsTextField
                            value={legal.pecConfirm || ''}
                            onChange={handlePECConfirmChange}
                            error={legal.pecConfirmValidity !== ''}
                            errorText={legal.pecConfirmValidity}
                            label="Conferma PEC*"
                            readOnly={legal.isPECReadOnly}
                            collapse
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <StyledPageComment>
                            I tuoi dati personali sono trattati in conformità all'informativa che puoi consultare al seguente link: <a href="https://tc.teamsystem.com/InformativaPrivacy.pdf" target="_blank" rel="noreferrer">https://tc.teamsystem.com/InformativaPrivacy.pdf</a>.
                            Per qualunque richiesta al riguardo puoi contattarci agli indirizzi riportati alla sezione "Chi siamo e come contattarci"
                        </StyledPageComment>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <VdsButton
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                        handleCreateLegalDialog(false);
                    }}
                >
                    <React.Fragment>
                        Chiudi
                    </React.Fragment>
                </VdsButton>
                <VdsButton
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        handleSubmit();
                    }}
                >
                    <React.Fragment>
                        Conferma
                    </React.Fragment>
                </VdsButton>
            </DialogActions>
        </VdsDialog>
    );
}

//#endregion
